import {
  Alert,
  Box,
  Button,
  Container,
  FormField,
  SpaceBetween,
  Spinner,
} from '@cloudscape-design/components';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import AutoCompleteSearch from '../../components/AutoCompleteSearch';
import { LoadingOrError } from '../../components/Loading';
import ReportCardHeader from '../../components/ReportCardHeader';
import { ViewLayout } from '../../components/ViewLayout';
import { PublicScorecardPreview } from '../../components/PublicScorecardPreview';
import {
  PublicScorecardFormHeader,
  PUBLIC_SCORECARD_SUBMIT_LABEL,
} from '../../components/PublicScorecardFormHeader';
import {
  createAdamPublicScorecard,
  validateAdamPublicScorecardLink,
} from '../../common/api/reports';
import { getBusinessType } from '../../common/businessTypes';
import type { Suggestion } from '../../common/useMaps';

type AdamPublicScorecardCreatePageProps = {
  code: string;
};

export default function AdamPublicScorecardCreatePage({
  code,
}: AdamPublicScorecardCreatePageProps) {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<Suggestion[]>([]);
  const selectedSuggestion = selectedItems[0];
  const linkQuery = useQuery({
    queryKey: ['adam-public-scorecard-link', code],
    queryFn: () => validateAdamPublicScorecardLink(code),
    enabled: code.length > 0,
    retry: false,
  });
  const createMutation = useMutation({
    mutationFn: (suggestion: Suggestion) =>
      createAdamPublicScorecard(code, {
        place_id: suggestion.placeId,
        business_type: getBusinessType(suggestion.tags) ?? undefined,
      }),
    onSuccess: (data) => {
      navigate({
        to: '/create/$publicCode',
        params: { publicCode: data.public_code },
        search: { completion: 'scorecard', delivery: undefined },
      });
    },
  });

  const layout = (content: ReactNode) => (
    <ViewLayout
      header={<ReportCardHeader showDemoCta={false} />}
      contentType="dashboard"
      navigationHide
      headerVariant="high-contrast"
    >
      {content}
    </ViewLayout>
  );

  if (!code || linkQuery.isError) {
    return layout(
      <Container>
        <Alert type="error" header="Scorecard link unavailable">
          Check that you have the complete link and try again.
        </Alert>
      </Container>,
    );
  }

  if (linkQuery.isLoading || !linkQuery.data) {
    return layout(
      <Container>
        <Box textAlign="center" padding="xxl">
          <Spinner size="large" />
          <Box padding="s">Loading scorecard creator...</Box>
        </Box>
      </Container>,
    );
  }

  return layout(
    <SpaceBetween direction="vertical" size="l">
      <Container
        header={<PublicScorecardFormHeader />}
      >
        <SpaceBetween direction="vertical" size="l">
          <FormField
            label="Business Location"
            description="Start typing the business name or location and select it from the list."
          >
            <AutoCompleteSearch
              selectedItems={selectedItems}
              setSelectedItems={(items) => setSelectedItems(items.slice(0, 1))}
              loading={createMutation.isPending}
              searchDisabled={selectedItems.length > 0}
              hideSearch={selectedItems.length > 0}
              placeholder="Enter a business name..."
            />
          </FormField>
          <Box float="right">
            <Button
              variant="primary"
              iconName="add-plus"
              onClick={() => selectedSuggestion && createMutation.mutate(selectedSuggestion)}
              loading={createMutation.isPending}
              disabled={!selectedSuggestion}
            >
              {PUBLIC_SCORECARD_SUBMIT_LABEL}
            </Button>
          </Box>
          {createMutation.isError && (
            <LoadingOrError loading={false} error={createMutation.error} />
          )}
        </SpaceBetween>
      </Container>
      {!createMutation.isPending && !createMutation.isSuccess && <PublicScorecardPreview />}
    </SpaceBetween>,
  );
}
